"use client";

import { sanitizeHtml } from "@offstreetparking/domain/html";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { type DOMAttributes, useMemo, useRef, useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../overlay/Tooltip";
import { classNames } from "../../utils/classNames";

export const textStyles = cva("", {
	variants: {
		intent: {
			body: "text-neutral",
			muted: "text-neutral-faded",
			primary: "text-primary",
			action: "text-accent underline",
		},
		size: {
			xs: "text-xs",
			sm: "text-sm",
			md: "text-base",
			lg: "text-lg",
			xl: "text-xl",
			"2xl": "text-2xl",
			"3xl": "text-3xl",
			"4xl": "text-4xl",
			"5xl": "text-5xl",
		},
		weight: {
			thin: "font-thin",
			normal: "font-normal",
			medium: "font-medium",
			semibold: "font-semibold",
			bold: "font-bold",
			bolder: "font-extrabold",
		},
	},
	defaultVariants: {
		intent: "body",
		size: "md",
		weight: "normal",
	},
});

export type TextProps = {
	children?: React.ReactNode;
	intent?: VariantProps<typeof textStyles>["intent"];
	size?: VariantProps<typeof textStyles>["size"];
	weight?: VariantProps<typeof textStyles>["weight"];
	As?: "p" | "span" | "div";
	className?: string;
	id?: string;
	onClick?: React.MouseEventHandler<HTMLElement>;
	dangerouslySetInnerHTML?: DOMAttributes<HTMLParagraphElement>["dangerouslySetInnerHTML"] & {
		onlyStyledText?: boolean;
	};
};

export const Text = ({
	children,
	intent,
	size,
	weight,
	As = "p",
	className,
	id,
	onClick,
	dangerouslySetInnerHTML,
}: TextProps) => {
	const [isOpen, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	const safeSetInnerHTML = useMemo(() => {
		if (dangerouslySetInnerHTML && typeof dangerouslySetInnerHTML === "object") {
			return {
				...dangerouslySetInnerHTML,
				__html: sanitizeHtml(dangerouslySetInnerHTML.__html?.toString(), {
					onlyStyledText: dangerouslySetInnerHTML.onlyStyledText ?? true,
				}),
			};
		}
		return undefined;
	}, [dangerouslySetInnerHTML]);

	return (
		<Tooltip open={isOpen}>
			<TooltipTrigger>
				{safeSetInnerHTML ? (
					<As
						id={id}
						ref={ref}
						onClick={onClick}
						onMouseEnter={() => {
							if ((ref.current?.offsetWidth ?? 0) < (ref.current?.scrollWidth ?? 0)) {
								setOpen(true);
							}
						}}
						onMouseLeave={() => setOpen(false)}
						dangerouslySetInnerHTML={safeSetInnerHTML}
						className={classNames(textStyles({ intent, size, weight }), className)}
					/>
				) : (
					<As
						id={id}
						ref={ref}
						onClick={onClick}
						onMouseEnter={() => {
							if ((ref.current?.offsetWidth ?? 0) < (ref.current?.scrollWidth ?? 0)) {
								setOpen(true);
							}
						}}
						onMouseLeave={() => setOpen(false)}
						className={classNames(textStyles({ intent, size, weight }), className)}
					>
						{children}
					</As>
				)}
			</TooltipTrigger>
			<TooltipContent>{children}</TooltipContent>
		</Tooltip>
	);
};
